// The native-device gateway contract (ADR 064 — Native Shell § 5, BL-2090):
// the one door from admin web code to a capability of the phone app it runs in.
import type { NativeTransferQueue } from './native-transfer/types';
import type { OfflineMediaStaging, OfflineScope, OfflineStore } from './offline-store/types';

/** Input to {@link NativeDeviceGateway.openAuthSession}. */
export interface OpenAuthSessionInput {
  /** The https URL the platform auth session opens (same origin as the admin site). */
  url: string;
}

/** Input to {@link NativeDeviceGateway.saveFile}. */
export interface SaveFileInput {
  /** The file's bytes; its `type` is the MIME the share sheet offers apps for. */
  blob: Blob;
  /** The name the file is saved or shared under, e.g. `Spring-Sale-Clerk-Sheet.pdf`. */
  filename: string;
}

/**
 * How a platform auth session ended. `completed` carries the custom-scheme URL
 * the browser sheet was redirected to; `cancelled` means the user closed the
 * sheet, which is not an error.
 */
export type AuthSessionOutcome =
  | { kind: 'completed'; callbackUrl: string }
  | { kind: 'cancelled' };

/** A native capability the admin web code can ask the phone app for. */
export type NativeCapability = 'openAuthSession' | 'offlineStore' | 'mediaStaging' | 'nativeTransfer';

/**
 * Thrown when the running shell can't provide a capability: in a browser, or in
 * an app build that predates the capability's native plugin. Callers catch it
 * and fall back to the web behaviour.
 */
export class NativeCapabilityUnavailableError extends Error {
  readonly capability: NativeCapability;

  /**
   * @param capability - the capability that is missing.
   */
  constructor(capability: NativeCapability) {
    super(`Native capability unavailable: ${capability}`);
    this.name = 'NativeCapabilityUnavailableError';
    this.capability = capability;
  }
}

/**
 * The phone-app capabilities admin web code may use. Obtain it through
 * `getNativeDeviceGateway()`; never test for the platform yourself.
 */
export interface NativeDeviceGateway {
  /**
   * @returns true when the page runs inside the native admin app's web view.
   */
  isNativeApp(): boolean;

  /**
   * Open the platform auth session (ASWebAuthenticationSession on iOS, Custom
   * Tabs on Android) at `url` and wait for it to finish.
   *
   * @param input - the https URL to open.
   * @returns the callback URL the sheet ended on, or a cancel.
   * @throws NativeCapabilityUnavailableError when this shell has no auth-session plugin.
   * @sideEffect Opens a system browser sheet over the app.
   */
  openAuthSession(input: OpenAuthSessionInput): Promise<AuthSessionOutcome>;

  /**
   * Hand a file to the user: a download in a browser, the phone's share sheet
   * (Save to Files, AirDrop, Print, …) inside the app (BL-2064). A web view
   * ignores `<a download>`, which is why every save goes through here.
   *
   * @param input - the file and the name to save it under.
   * @returns once the download started or the share sheet closed; closing the
   *   sheet without choosing anything is not an error.
   * @throws when the file cannot be written or the share sheet fails.
   * @sideEffect Browser: starts a download. App: writes the file to the app's
   *   cache and opens the share sheet over the app.
   */
  saveFile(input: SaveFileInput): Promise<void>;

  /**
   * Open the offline device store for one tenant and user (BL-2340, ADR 066 — Offline Mode § 2).
   * Only that scope's records, thumbnails and change queue are readable through it. Sign-out and
   * tenant switch wipe nothing; opening as a different user than the device's last one wipes
   * everything first, and a scope not opened for 30 days is wiped (§ 6.1, limit 3).
   *
   * @param scope - the tenant and the signed-in user.
   * @returns the store bound to that scope.
   * @throws NativeCapabilityUnavailableError('offlineStore') in a browser, which writes nothing.
   * @sideEffect App: opens IndexedDB; may wipe another user's or an idle scope.
   */
  openOfflineStore(scope: OfflineScope): Promise<OfflineStore>;

  /**
   * Open media staging for one tenant and user (BL-2421, ADR 066 — Offline Mode § 2): photos,
   * videos and documents picked offline, kept on the device filesystem until the change queue
   * uploads them. It shares the offline store's scope and lifetime: kept across sign-out and
   * tenant switch, wiped with the store when a different user opens it or the scope idles 30 days.
   *
   * @param scope - the tenant and the signed-in user.
   * @returns the staging bound to that scope.
   * @throws NativeCapabilityUnavailableError('mediaStaging') in a browser, or in an app build
   *   without the Filesystem plugin; nothing is written.
   * @sideEffect App: opens the offline store (may wipe another user's or an idle scope) and sweeps
   *   this scope's unfinished or orphaned staged files left by an earlier launch.
   */
  openMediaStaging(scope: OfflineScope): Promise<OfflineMediaStaging>;

  /**
   * Open the native transfer queue for one tenant and user (BL-2678, ADR 070 — Ingest Protocol § 7):
   * work the phone app uploads and downloads in the background, even with the phone locked.
   *
   * @param scope - the tenant and the signed-in user.
   * @returns the queue bound to that scope.
   * @throws NativeCapabilityUnavailableError('nativeTransfer') in a browser, the emulation, or an
   *   app build without the AuctogonNativeTransfer plugin; nothing is written.
   * @sideEffect App: opens the native queue database.
   */
  openNativeTransfer(scope: OfflineScope): Promise<NativeTransferQueue>;

  /**
   * The device's active tenant and user (BL-2343, ADR 066 — Offline Mode § 2): the scope an offline
   * cold start opens before any session exists.
   *
   * @returns the active scope, or null when none is set.
   * @throws NativeCapabilityUnavailableError('offlineStore') in a browser.
   */
  readActiveOfflineScope(): Promise<OfflineScope | null>;

  /**
   * Point the device at a scope, or clear the pointer (sign-out). Wipes nothing (§ 6.1 limit 3).
   *
   * @param scope - the signed-in tenant and user, or null.
   * @returns once written.
   * @throws NativeCapabilityUnavailableError('offlineStore') in a browser, which writes nothing.
   * @sideEffect App: writes the device-level pointer in IndexedDB.
   */
  setActiveOfflineScope(scope: OfflineScope | null): Promise<void>;

  /**
   * Listen for the app being opened from a link it claims: a scanned lot label's `/qr` link
   * (BL-2416, ADR 064 — Native Shell § 6). A link that cold-started the app is delivered to the
   * first listener. Callers decide which links they act on.
   *
   * @param listener - called with each opening URL.
   * @returns an unsubscribe; calling it before the native listener attaches still detaches it.
   *   In a browser, or an app build without the App plugin, nothing is ever delivered.
   */
  onAppLinkOpen(listener: (url: string) => void): () => void;

  /**
   * Listen for a tap on a native-transfer notification (BL-2685): the route key it carried, e.g.
   * `storage-sync`. A tap that cold-started the app is delivered to the first listener. Callers
   * decide which routes they act on.
   *
   * @param listener - called with each tapped notification's route key.
   * @returns an unsubscribe; calling it before the native listener attaches still detaches it.
   *   In a browser, or an app build without the native transfer plugin, nothing is ever delivered.
   */
  onNotificationRoute(listener: (route: string) => void): () => void;
}
